/**
 * Firebase Web (Cloud Messaging) client config.
 *
 * Values come from REACT_APP_FIREBASE_* env vars (public client identifiers,
 * safe to expose — see .env.example). This mirrors the Android FCM setup
 * (google-services.json) but for browsers.
 */

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

function hasFirebaseConfig() {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId,
  );
}

let messagingPromise = null;

/**
 * Lazily initialises Firebase + Messaging, returns null on unsupported
 * browsers (Safari without PWA install, private browsing, etc.) or when
 * config is missing, instead of throwing.
 */
export function getMessagingInstanceSafe() {
  if (!hasFirebaseConfig()) {
    return Promise.resolve(null);
  }

  if (!messagingPromise) {
    messagingPromise = (async () => {
      try {
        const { initializeApp } = await import("firebase/app");
        const { getMessaging, isSupported } = await import("firebase/messaging");

        const supported = await isSupported().catch(() => false);
        if (!supported) return null;

        const app = initializeApp(firebaseConfig);
        return getMessaging(app);
      } catch (err) {
        console.error("[Firebase] Init failed:", err?.message || err);
        return null;
      }
    })();
  }

  return messagingPromise;
}

export default firebaseConfig;
